import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import NetworkDiagram from './components/NetworkDiagram';
import FilterControls from './components/FilterControls';
import PathAnalysis from './components/PathAnalysis';
import SummaryStats from './components/SummaryStats';

const FundFlowMapping = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedAddress, setSelectedAddress] = useState('');
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterSettings, setFilterSettings] = useState({});
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [viewMode, setViewMode] = useState('network'); // 'network', 'paths', 'summary'

  // Get address from navigation state if coming from Transaction Analysis
  useEffect(() => {
    if (location?.state?.address) {
      setSelectedAddress(location?.state?.address);
      setAnalysisStarted(true);
    }
  }, [location?.state]);

  const handleAddressSubmit = async (e) => {
    e?.preventDefault();
    if (!selectedAddress?.trim()) return;
    
    setIsLoading(true);
    // Simulate analysis loading
    setTimeout(() => {
      setAnalysisStarted(true);
      setIsLoading(false);
    }, 2000);
  };

  const handleFiltersChange = (filters) => {
    setFilterSettings(filters);
  };

  const handleFiltersReset = (resetFilters) => {
    setFilterSettings(resetFilters);
  };

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
  };

  const handlePathSelect = (path) => {
    setSelectedPath(path);
    setViewMode('paths');
  };

  const handleExportDiagram = (svgElement) => {
    // Mock export functionality
    console.log('Exporting diagram...', svgElement);
  };

  const handleExportPath = (path) => {
    // Mock export functionality
    console.log('Exporting path...', path);
  };

  const handleNavigateToRiskAssessment = () => {
    if (selectedNode) {
      navigate('/address-risk-assessment', { 
        state: { address: selectedNode?.address } 
      });
    }
  };

  const handleNavigateToForensicReport = () => {
    navigate('/forensic-report-generator', { 
      state: { 
        address: selectedAddress,
        analysisData: {
          networkData: selectedNode,
          pathData: selectedPath,
          filterSettings
        }
      } 
    });
  };

  const resetAnalysis = () => {
    setSelectedAddress('');
    setAnalysisStarted(false);
    setSelectedNode(null);
    setSelectedPath(null);
    setFilterSettings({});
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Fund Flow Mapping
                </h1>
                <p className="text-text-secondary">
                  Visualize complex cryptocurrency movement patterns and identify fund destinations across multiple blockchain hops
                </p>
              </div>
              
              {analysisStarted && (
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={resetAnalysis}
                  >
                    <Icon name="RotateCcw" size={16} className="mr-2" />
                    New Analysis
                  </Button>
                  
                  {selectedNode && (
                    <Button
                      variant="outline"
                      onClick={handleNavigateToRiskAssessment}
                    >
                      <Icon name="Shield" size={16} className="mr-2" />
                      Risk Assessment
                    </Button>
                  )}
                  
                  <Button
                    variant="default"
                    onClick={handleNavigateToForensicReport}
                  >
                    <Icon name="FileText" size={16} className="mr-2" />
                    Generate Report
                  </Button>
                </div>
              )}
            </div>
          </div>

          {!analysisStarted ? (
            /* Initial Address Input */
            (<div className="max-w-2xl mx-auto">
              <div className="bg-surface rounded-lg border border-border shadow-forensic-card p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="GitBranch" size={32} color="white" />
                  </div>
                  <h2 className="text-2xl font-bold text-text-primary mb-2">
                    Start Fund Flow Analysis
                  </h2>
                  <p className="text-text-secondary">
                    Enter a cryptocurrency address to begin tracing fund movements and identifying transaction patterns
                  </p>
                </div>

                <form onSubmit={handleAddressSubmit} className="space-y-6">
                  <Input
                    label="Cryptocurrency Address"
                    type="text"
                    placeholder="Enter Bitcoin, Ethereum, or other crypto address..."
                    value={selectedAddress}
                    onChange={(e) => setSelectedAddress(e?.target?.value)}
                    required
                    description="Supports Bitcoin, Ethereum, Litecoin, and other major cryptocurrencies"
                  />
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-text-secondary">
                    <div className="flex items-center space-x-2">
                      <Icon name="Search" size={16} className="text-primary" />
                      <span>Multi-hop transaction tracing</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Shield" size={16} className="text-primary" />
                      <span>Risk assessment integration</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="FileText" size={16} className="text-primary" />
                      <span>Forensic report generation</span>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    loading={isLoading}
                    disabled={!selectedAddress?.trim()}
                  >
                    {isLoading ? 'Analyzing Fund Flow...' : 'Start Analysis'}
                  </Button>
                </form>

                {/* Recent Analyses */}
                <div className="mt-8 pt-8 border-t border-border">
                  <h3 className="text-sm font-semibold text-text-primary mb-4">
                    Recent Analyses
                  </h3>
                  <div className="space-y-2">
                    {[
                      '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
                      '3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy',
                      'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh'
                    ]?.map((address, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedAddress(address)}
                        className="w-full text-left p-3 rounded-lg border border-border hover:bg-muted transition-forensic"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm text-text-primary truncate">
                            {address}
                          </span>
                          <Icon name="ChevronRight" size={16} className="text-text-secondary" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>)
          ) : (
            /* Analysis Interface */
            (<div className="space-y-6">
              {/* View Mode Tabs */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                  <Button
                    variant={viewMode === 'network' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('network')}
                  >
                    <Icon name="GitBranch" size={16} className="mr-2" />
                    Network View
                  </Button>
                  <Button
                    variant={viewMode === 'paths' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('paths')}
                  >
                    <Icon name="Route" size={16} className="mr-2" />
                    Path Analysis
                  </Button>
                  <Button
                    variant={viewMode === 'summary' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('summary')}
                  >
                    <Icon name="BarChart3" size={16} className="mr-2" />
                    Summary
                  </Button>
                </div>

                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon name="MapPin" size={16} />
                  <span className="font-mono">{selectedAddress}</span>
                </div>
              </div>
              {/* Filter Controls */}
              <FilterControls
                onFiltersChange={handleFiltersChange}
                onReset={handleFiltersReset}
              />
              {/* Main Content Area */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {viewMode === 'network' && (
                  <>
                    {/* Network Diagram */}
                    <div className="lg:col-span-2">
                      <NetworkDiagram
                        selectedAddress={selectedAddress}
                        filterSettings={filterSettings}
                        onNodeSelect={handleNodeSelect}
                        onPathHighlight={setSelectedPath}
                        exportDiagram={handleExportDiagram}
                      />
                    </div>

                    {/* Summary Stats Sidebar */}
                    <div className="space-y-6">
                      <SummaryStats 
                        analysisData={{
                          selectedAddress,
                          filterSettings,
                          selectedNode,
                          selectedPath
                        }}
                      />
                      
                      {selectedNode && (
                        <div className="bg-surface rounded-lg border border-border shadow-forensic-card p-4">
                          <h3 className="text-lg font-semibold text-text-primary mb-4">
                            Selected Address
                          </h3>
                          <div className="space-y-3 text-sm">
                            <div>
                              <span className="text-text-secondary">Address:</span>
                              <p className="font-mono text-text-primary break-all mt-1">
                                {selectedNode?.address}
                              </p>
                            </div>
                            <div>
                              <span className="text-text-secondary">Type:</span>
                              <p className="text-text-primary capitalize mt-1">
                                {selectedNode?.type}
                              </p>
                            </div>
                            <div>
                              <span className="text-text-secondary">Amount:</span>
                              <p className="text-text-primary font-semibold mt-1">
                                {selectedNode?.amount} BTC
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              fullWidth
                              onClick={handleNavigateToRiskAssessment}
                            >
                              <Icon name="Shield" size={16} className="mr-2" />
                              Assess Risk
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {viewMode === 'paths' && (
                  <div className="lg:col-span-3">
                    <PathAnalysis
                      selectedPath={selectedPath}
                      onPathSelect={handlePathSelect}
                      onExportPath={handleExportPath}
                    />
                  </div>
                )}

                {viewMode === 'summary' && (
                  <div className="lg:col-span-3">
                    <SummaryStats 
                      analysisData={{
                        selectedAddress,
                        filterSettings,
                        selectedNode,
                        selectedPath
                      }}
                    />
                  </div>
                )}
              </div>
            </div>)
          )}
        </div>
      </main>
    </div>
  );
};

export default FundFlowMapping;